import React, { useMemo, useState } from "react";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox, { CheckboxProps } from "@mui/material/Checkbox";
import { FieldMetaProps } from "formik";
import { FieldError } from "./FieldError";
import classNames from "classnames";

export interface ICheckboxFieldProps extends CheckboxProps {
  "data-testid"?: string;
  input?: any;
  custom?: any;
  name?: string;
  label?: string;
  labelPlacement?: "end" | "start" | "top" | "bottom";
  isDisabled?: boolean;
  disabled?: boolean;
  className?: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => void;
  checked?: boolean;
  hideFocus?: boolean;
  meta?: Partial<FieldMetaProps<any>>;
}

// CheckboxField COMPONENT
export const CheckboxField = (props: ICheckboxFieldProps): JSX.Element => {
  const {
    "data-testid": dataTestId,
    label,
    name,
    value,
    onFocus,
    onBlur,
    labelPlacement = "start",
    isDisabled,
    disabled,
    className,
    checked,
    onChange,
    hideFocus,
    meta,
  } = props;
  const [isFocused, setIsFocused] = useState<boolean>(false);

  const internalValue = useMemo(() => (value === true || value === false ? !!value : !!checked), [value, checked]);

  // Track if formik value is dirty.
  const dirty: boolean = useMemo(() => {
    if (!meta) return false;
    const { initialValue, touched } = meta;
    return touched && internalValue !== initialValue ? true : false;
  }, [meta, internalValue]);

  const handleFocus = (e: React.FocusEvent<HTMLButtonElement>): void => {
    setIsFocused(true);
    // Uses props onfocus (as formik doesn't supply one)
    if (onFocus && typeof onFocus === "function") {
      onFocus(e);
    }
  };

  // Custom blur handler for bug workaround due to input value
  // resetting when using search feature onBlur.
  const handleBlur = (e: React.FocusEvent<HTMLButtonElement>): void => {
    setIsFocused(false);
    if (onBlur && typeof onBlur === "function") {
      onBlur(e);
    }
  };

  const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    // Fire off the supplied onChange from props and not the formik field one (it breaks as it wants an event and not a value)
    if (onChange && typeof onChange === "function") {
      onChange(event, !internalValue);
    }
  };

  const hasError = meta?.error ? true : false;

  const checkboxFieldStyles = (theme: any): any => ({
    root: {
      display: "flex",
      flexDirection: "column",
      margin: "5px",
      flexWrap: "wrap",
      width: "calc(100% - 10px)",
      "&.dirty": {
        "& .label, & svg": { color: theme.custom.forms.dirtyColor },
        "& .checkbox:before": { display: "none" },
      },
      "&.focused": {
        "& .label, & svg": { color: theme.custom.forms.focusedColor },
        "& .checkbox:before": { display: "none" },
      },
      "&.checked": {
        "& svg": { color: theme.palette.primary.main },
        "& .checkbox:before": { display: "none" },
      },
      "&.nolabel": {
        width: "auto",
        "& .checkbox": { padding: 0 },
      },
      "&.logsheetCheck": {
        textAlign: "center",
        color: theme.palette.primary.main,
        "& svg": {
          color: theme.palette.primary.main,
        },
      },
      "& .checkboxControl": {
        display: "flex",
        justifyContent: "center",
        margin: 0,
        "& .label": {
          userSelect: "none",
          width: "auto",
          height: "auto",
          position: "relative",
          textAlign: labelPlacement === "start" ? "left" : "right",
          transform: "none",
          transition: "none",
          color: theme.custom.forms.pristineColor,
          fontSize: 12,
          lineHeight: "12px",
          margin: "5px 10px auto auto",
        },
        "& span": { padding: 0, height: "24px", width: "24px" },
        "& svg": {
          color: theme.custom.forms.pristineColor,
          transition: theme.transitions.create("color"),
        },
        "&:hover svg": {
          fill: theme.custom.forms.pristineColor,
        },
        "& .checkbox:before": {
          position: "absolute",
          content: '""',
          width: "16px",
          height: "16px",
          backgroundColor: theme.custom.forms.inputBack,
        },
      },
      "&.disabled": {
        userSelect: "none",
        pointerEvents: "none",
        "& .checkboxControl .MuiCheckbox-root": {
          opacity: 0.5,
        },
      },
    },
  });

  return (
    <FormControl
      data-testid={dataTestId}
      className={classNames(
        className,
        internalValue && "checked",
        (isDisabled || disabled) && "disabled",
        isFocused && !hideFocus && "focused",
        dirty && "dirty",
        hasError && "error",
        !label && "nolabel"
      )}
      sx={(theme) => checkboxFieldStyles(theme).root}
      error={hasError}
    >
      <div className="input_control">
        {label ? (
          <FormControlLabel
            classes={{
              root: "checkboxControl",
              label: "label",
            }}
            label={label}
            labelPlacement={labelPlacement}
            control={
              <Checkbox
                id={name}
                name={name}
                checked={internalValue}
                classes={{ root: "checkbox" }}
                onChange={handleOnChange}
                onBlur={handleBlur}
                onFocus={handleFocus}
                disabled={isDisabled || disabled}
              />
            }
          />
        ) : (
          <Checkbox
            id={name}
            name={name}
            checked={internalValue}
            classes={{ root: "checkbox" }}
            onChange={handleOnChange}
            onBlur={handleBlur}
            onFocus={handleFocus}
            disabled={isDisabled || disabled}
          />
        )}
      </div>
      <FieldError meta={meta} />
    </FormControl>
  );
};
